/**
 * Where a site link goes (site completion). A country pack names a destination with a neutral `SiteTarget` token
 * (`@vello/web-entry`); this resolves it to what the browser does — an in-site page (optionally a section of it), a legal
 * text, the Central chat, or an outside address. Pure: no DOM, no market or language literal. `web/` renders it.
 */
import type { SiteTarget } from '@vello/web-entry';
import { isSitePage, type SitePage } from './site-routes.js';

export type ResolvedSiteTarget =
  | { readonly kind: 'page'; readonly page: SitePage; readonly section?: string }
  | { readonly kind: 'legal'; readonly document: 'TERMS_OF_USE' | 'PRIVACY_POLICY' }
  | { readonly kind: 'contact' }
  | { readonly kind: 'external'; readonly href: string }
  | { readonly kind: 'invalid' };

const SECTION = /^[a-z][a-z0-9-]{0,40}$/;
const EXTERNAL = /^(?:https:\/\/[^\s/?#]+(?:[/?#][^\s]*)?|mailto:[^\s@]+@[^\s@]+)$/;

/** Resolve a pack's target token. Anything unrecognised is `invalid` (the site then renders the label without a link). */
export function resolveSiteTarget(target: SiteTarget): ResolvedSiteTarget {
  const value = target.trim();
  if (value === 'contact') return { kind: 'contact' };
  if (value === 'legal:terms') return { kind: 'legal', document: 'TERMS_OF_USE' };
  if (value === 'legal:privacy') return { kind: 'legal', document: 'PRIVACY_POLICY' };
  if (EXTERNAL.test(value)) return { kind: 'external', href: value };
  const [page = '', section, ...extra] = value.split('#');
  if (extra.length > 0 || !isSitePage(page)) return { kind: 'invalid' };
  if (section === undefined || section === '') return { kind: 'page', page };
  return SECTION.test(section) ? { kind: 'page', page, section } : { kind: 'invalid' };
}

/**
 * The driver simulator's estimate, in the currency's minor units: hours × the reference hour, rounded to a whole major
 * unit (the page shows no cents on an estimate). `minorPerMajor` is 10^(the currency's exponent). For a daily slider the
 * caller passes `hours a day × the days the result covers` (D628: a month of daily hours).
 */
export function simulatorEstimateMinor(hours: number, hourlyMinor: number, minorPerMajor: number): number {
  if (!Number.isFinite(hours) || !Number.isFinite(hourlyMinor) || hours <= 0 || hourlyMinor <= 0 || minorPerMajor <= 0) return 0;
  return Math.round((hours * hourlyMinor) / minorPerMajor) * minorPerMajor;
}
